import { useCallback } from 'react';

import { type OrderingMachineAction } from '@order/features-ordering';

import { useResetGroupOrderingState } from '../../../../hooks/useGroupOrderingState';
import { storeDismissedGroupOrderId } from '../../../../hooks/useGroupOrderingState/group-order-reference-storage';
import { useGroupOrderTelemetry } from '../../../../hooks/useGroupOrderTelemetry';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the group-order leave success action that closes Bag and clears
 * participant state while preserving the host's location after cancellation.
 */
export const useHandleLeaveGroupOrderSuccess = (params: Params) => {
  const { closeBag } = params;

  const resetGroupOrderingState = useResetGroupOrderingState();
  const { trackCancelSuccess, trackLeaveSuccess } = useGroupOrderTelemetry();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLeaveGroupOrderSuccess = useCallback<OrderingMachineAction>(
    ({ context }) => {
      closeBag();

      const isHost = Boolean(context.cart.canSubmitOrder);

      if (isHost) {
        trackCancelSuccess();

        return;
      }

      trackLeaveSuccess();

      // Forget the order so a later host submission or cancellation does not
      // surface a terminal-state message for an order this participant left.
      if (context.cart.orderId) {
        storeDismissedGroupOrderId(context.cart.orderId);
      }

      resetGroupOrderingState();
    },
    [closeBag, resetGroupOrderingState, trackCancelSuccess, trackLeaveSuccess],
  );

  return handleLeaveGroupOrderSuccess;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  closeBag: () => void;
};
